import { ref } from "vue";

const CLEAR_ERROR_TIMEOUT = 10000
const MAX_AUDIO_IN_BYTES = 25000000 //26214400 is 25 * 1024 * 1024, but that appears to be too big
const TRANSCRIPTION_INTERVAL = 2000
const TRANSCRIPTION_URL = 'https://speechtotext.choicecloud.healthcare/transcribe'

export function useSpeechToText(){
    let mediaRecorder:MediaRecorder
    let audioChunks:BlobPart[] =  []
    let isTranscribing = ref<boolean>(false)
    const transcription = ref<string>("")
    const transcriptionError = ref<string>("")
    const audioAllowed = ref<boolean>(true)
    let errorCount = 0

    //@ts-ignore microphone is legit for name, but not in lib.dom.d.ts for whatever reason. Hooray typescript
    // navigator.permissions.query({name:"microphone"}).then(resp => {
    //     if ("denied" === resp.state ){
    //         audioAllowed.value = false
    //     }
    // })

    function onAudioAllowed(stream:MediaStream){
        audioAllowed.value = true
        mediaRecorder = new MediaRecorder(stream)
        mediaRecorder.ondataavailable = async event => await transcribe(event)
        mediaRecorder.onstop = _ => audioChunks = []
        mediaRecorder.start(TRANSCRIPTION_INTERVAL)
        isTranscribing.value = true
        errorCount = 0
    }

    function onAudioDisallowed(){
        audioAllowed.value = false
        stopAudioRecording()
    }

    function startAudioRecording(){
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(onAudioAllowed)
            .catch(onAudioDisallowed)
    }

    function stopAudioRecording(){
        if (mediaRecorder){
            mediaRecorder.stop()
        }
        isTranscribing.value = false
        
        // Clear the transcription error after 10 seconds. Primarly for UI purposes.
        setTimeout(() => transcriptionError.value = "", CLEAR_ERROR_TIMEOUT)
    }

    function setTranscriptionError(text: string){
        transcriptionError.value = text
        errorCount++
    }

    async function transcribe(audio: BlobEvent){
        if (!isTranscribing){
            return
        }

        audioChunks.push(audio.data)

        try {
            const currentTranscription = new Blob(audioChunks)
            if (MAX_AUDIO_IN_BYTES < currentTranscription.size){
                const maximumRecordingSizeExceededMessage = "The current audio recording exceeds the maxmium of 25MB. Transcription has been terminated. Re-start transcription to continue."
                console.error(maximumRecordingSizeExceededMessage)
                setTranscriptionError(maximumRecordingSizeExceededMessage)
                stopAudioRecording()
                return
            }

            const formData = new FormData();
            formData.append('audio', currentTranscription, `${crypto.randomUUID()}.webm`);

            const transcriptionResponse = await fetch(TRANSCRIPTION_URL, { method: 'POST', body: formData })
            // Handle bad responses from speech to text server
            if (!transcriptionResponse.ok){
                let invalidResponseMessage = "Received an invalid response from the transcription service."
                if (errorCount >= 3){
                    // occasional transcription errors are not unusual. But if we encounter a number of them during a single transcription, its possible something
                    // is wrong or the server is overloaded.
                    invalidResponseMessage = invalidResponseMessage + " Encountered 3 or more errors from transcription server while recording. Ending transcription."
                    stopAudioRecording()
                }
                console.error(invalidResponseMessage)
                setTranscriptionError(invalidResponseMessage)
                return
            }
            const result = await transcriptionResponse.json()
            transcription.value = result.text
        } catch (error) {
            // Handle unknown errors
            const unhandledErrorMessage = "An unhandled error occurred attempting to transcribe speech."
            console.error(unhandledErrorMessage, error)
            setTranscriptionError(unhandledErrorMessage)
            stopAudioRecording() // stop recording because we don't know what happened
        }
    }

    return {
        startAudioRecording,
        stopAudioRecording,
        transcription,
        transcriptionError,
        isTranscribing,
        audioAllowed
    }
}
